import config from '../config';
import { Sim } from '../models/sim';
import { UserSetting } from '../models/state/userSettings';
import moment from 'moment';
import { inTimeZone } from './timeZone';
import { getUniqueResolvedFieldLabels } from './fieldLabels';

export function getApiBaseUrl(companyId: number, accountId?: number | null) {
  let url = `${config.API_BASE_URL}api/company/${companyId}/`;
  if (accountId) {
    url += `account/${accountId}/`;
  }

  return url;
}

export function getBaseUrl(companyId: number, accountId?: number | null) {
  let url = `/company/${companyId}/`;
  if (accountId) {
    url += `account/${accountId}/`;
  }

  return url;
}

export function getUserSetting(settingName: string, userSettings: Array<UserSetting>) {
  const matchingUserSettings = userSettings.filter(
    (userSetting) => userSetting.name.toLowerCase() === settingName.toLowerCase()
  );

  return matchingUserSettings.length > 0 ? matchingUserSettings[0] : null;
}

export function getUserSettingValueOrDefault(settingName: string, userSettings: Array<UserSetting>, defaultValue: any) {
  const matchingUserSettings = userSettings.find(
    (userSetting) => userSetting.name.toLowerCase() === settingName.toLowerCase()
  );

  if (matchingUserSettings?.settingValue) {
    try {
      return JSON.parse(matchingUserSettings.settingValue);
    } catch {
      return defaultValue;
    }
  }

  return defaultValue;
}

export function getMaxWidth(rows: Array<any>, property: string): number {
  return rows.reduce(
    (width: number, row: any) => Math.max(width, Math.ceil((row?.[property]?.length || 0) * 1.1)),
    Math.ceil(property.length * 1.1)
  );
}

export function formatDateTimeWithUserTimeZone(value: Date, userSettings: Array<UserSetting>) {
  const timeZone = getUserSetting('timezone', userSettings)?.settingValue;
  const format = 'DD-MM-YYYY, HH:mm:ss';

  if (timeZone) {
    return inTimeZone(value, timeZone).format(format);
  } else {
    return moment(value).format(format);
  }
}

export function formatDateWithUserTimeZone(value: Date, userSettings: Array<UserSetting>) {
  const timeZone = getUserSetting('timezone', userSettings)?.settingValue;
  const format = 'DD-MM-YYYY';

  if (timeZone) {
    return inTimeZone(value, timeZone).format(format);
  } else {
    return moment(value).format(format);
  }
}

export async function exportSimsTable(sims: Array<Sim>, userSettings: Array<UserSetting>, subportalUser: boolean = true) {
  const userSettingsColumnOrder = getUserSetting('columnOrder', userSettings)?.settingValue;
  const userSettingsColumnsToHide = getUserSetting('columnsToHide', userSettings)?.settingValue;
  const uniqueFieldLabels = getUniqueResolvedFieldLabels([
    'sim.status',
    'sim.legacyStatus',
    'sim.serialNumber',
    'sim.prefix',
    'sim.tel',
    'sim.network',
    'sim.accountId',
    'sim.accountTitle',
    'sim.raNumber',
    'sim.tariff',
    'sim.commencementDate',
    'sim.provisioningOrder',
    'sim.order1',
    'sim.order2',
    'sim.order3',
    'sim.order4',
    'sim.order5',
    'sim.simType',
    'sim.dataBundleMb',
    'sim.dataUsageMb',
    'sim.voiceMinutes',
    'sim.smsCount',
    'sim.activeBundle',
    'sim.operationInProgress',
    'sim.iccid',
    'sim.privateIp',
    'sim.publicIp',
    'sim.imei',
    'sim.alertManagerEnabled',
    'sim.subportal'
  ]);

  const allColumns: Partial<Record<keyof Sim, string>> = {
    status: uniqueFieldLabels['sim.status'],
    legacyStatus: uniqueFieldLabels['sim.legacyStatus'],
    serialNumber: uniqueFieldLabels['sim.serialNumber'],
    prefix: uniqueFieldLabels['sim.prefix'],
    tel: uniqueFieldLabels['sim.tel'],
    network: uniqueFieldLabels['sim.network'],
    accountId: uniqueFieldLabels['sim.accountId'],
    accountTitle: uniqueFieldLabels['sim.accountTitle'],
    raNumber: uniqueFieldLabels['sim.raNumber'],
    tariff: uniqueFieldLabels['sim.tariff'],
    commencementDate: uniqueFieldLabels['sim.commencementDate'],
    provisioningOrder: uniqueFieldLabels['sim.provisioningOrder'],
    order1: uniqueFieldLabels['sim.order1'],
    order2: uniqueFieldLabels['sim.order2'],
    order3: uniqueFieldLabels['sim.order3'],
    order4: uniqueFieldLabels['sim.order4'],
    order5: uniqueFieldLabels['sim.order5'],
    simType: uniqueFieldLabels['sim.simType'],
    dataBundleMb: uniqueFieldLabels['sim.dataBundleMb'],
    dataUsageMb: uniqueFieldLabels['sim.dataUsageMb'],
    voiceMinutes: uniqueFieldLabels['sim.voiceMinutes'],
    smsCount: uniqueFieldLabels['sim.smsCount'],
    activeBundle: uniqueFieldLabels['sim.activeBundle'],
    operationInProgress: uniqueFieldLabels['sim.operationInProgress'],
    iccid: uniqueFieldLabels['sim.iccid'],
    privateIp: uniqueFieldLabels['sim.privateIp'],
    publicIp: uniqueFieldLabels['sim.publicIp'],
    imei: uniqueFieldLabels['sim.imei'],
    alertManagerEnabled: uniqueFieldLabels['sim.alertManagerEnabled'],
    subportal: uniqueFieldLabels['sim.subportal'],
    // the grid hard-codes this header rather than resolving a field label, so match it
    tags: 'Tags'
  };

  const columnOrder: Array<string> = userSettingsColumnOrder
    ? JSON.parse(userSettingsColumnOrder)
    : [
        'status',
        'legacyStatus',
        'serialNumber',
        'prefix',
        'tel',
        'network',
        'tariff',
        'dataBundleMb',
        'dataUsageMb',
        'voiceMinutes',
        'smsCount',
        'order1',
        'order2',
        'accountId',
        'accountTitle',
        'commencementDate',
        'lastCommand',
        'provisioningOrder',
        'raNumber'
      ];

  let columnsToHide: Array<string> = userSettingsColumnsToHide
    ? JSON.parse(userSettingsColumnsToHide)
    : ['id', 'companyId', 'networkId', 'order3', 'order4', 'order5', 'selected', 'activeBundle', 'iccid', 'imei', 'prefix'];

  let allColumnOrder = [
    ...columnOrder.filter((col) => Object.keys(allColumns).includes(col) && !columnsToHide.includes(col)),
    ...Object.keys(allColumns)
      .filter((col) => !columnOrder.includes(col) && !columnsToHide.includes(col))
      .sort()
  ];

  if (subportalUser) {
    if (!columnsToHide.includes('accountTitle')) {
      columnsToHide.push('accountTitle');
    }

    allColumnOrder = allColumnOrder.filter((x) => x !== 'accountTitle');
  }

  const sheetData = sims.map((sim) => {
    const row: Record<string, any> = {};

    allColumnOrder.forEach((col) => {
      // tags are objects, so a raw cell would export as [object Object]
      const value = col === 'tags' ? (sim.tags ?? []).map((tag) => tag.name).join(', ') : (sim as any)[col];
      row[(allColumns as any)[col] || col] = value;
    });

    return row;
  });

  const { utils, writeFileXLSX } = await import('xlsx');
  const sheet = utils.json_to_sheet(sheetData, {});
  sheet['!cols'] = allColumnOrder.map((col) => ({ wch: getMaxWidth(sheetData, (allColumns as any)[col] || col) }));

  const book = utils.book_new();
  utils.book_append_sheet(book, sheet, 'SIMsExport');

  const date = new Date();
  writeFileXLSX(
    book,
    `SIMsExport (${date.getUTCHours()}.${date.getUTCMinutes()} - ${date.getUTCDate()}.${date.getUTCMonth() + 1}.${date.getUTCFullYear()}).xlsx`
  );
}
export const validateEmail = (email: string) => {
  return /.+@.+[.].+/.test(email.trim());
};

export const GetNetworkIdFromSelectedSim = (selectedSims: any): number => {
  let networkId = -1;
  for (const property in selectedSims) {
    if (selectedSims.hasOwnProperty(property)) {
      const sim = selectedSims[property];
      if (sim.networkId) {
        networkId = sim.networkId;
        break;
      }
    }
  }

  return networkId;
};

export const getSimList = (chosenSims: any) => {
  let sims = [];
  for (const property in chosenSims) {
    if (chosenSims.hasOwnProperty(property)) {
      let sim = chosenSims[property];
      sims.push(sim);
    }
  }
  return sims;
};
